import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Paper
} from '@mui/material';
import { Print as PrintIcon, Close as CloseIcon } from '@mui/icons-material';
import { formatCurrency, formatDate } from '../../../utils/compteClientEngine';
import { COLORS } from '../../../settings/themeSettings';

const RecuEncaissementModal = ({ open, onClose, recuData }) => {
    if (!recuData) return null;

    const {
        numeroRecu = 'E-2026-0001',
        clientName = 'Client',
        dateEncaissement = new Date(),
        modePaiement = 'Espèces',
        reference = '-',
        montantEncaisse = 0,
        utilisateur = 'Administrateur',
        lignes = []
    } = recuData;

    const dateTirage = new Date().toLocaleDateString('fr-FR') + ' à ' + new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

    const handlePrint = () => {
        window.print();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#0f172a', color: 'white', py: 1.5 }}>
                <Typography variant="h6" fontWeight="bold">
                    Reçu d'encaissement {numeroRecu}
                </Typography>
                <Button size="small" onClick={onClose} sx={{ color: 'white' }}>
                    <CloseIcon />
                </Button>
            </DialogTitle>

            <DialogContent sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f8fafc' }}>
                <Paper
                    elevation={0}
                    className="printable-document"
                    sx={{
                        p: 4,
                        border: '2px solid #0f172a',
                        borderRadius: '8px',
                        bgcolor: 'white',
                        maxWidth: '800px',
                        mx: 'auto'
                    }}
                >
                    {/* EN-TÊTE TRANSVERSAL COMMUN (CONFORME SECTION 1 & 4.7) */}
                    <Box sx={{ borderBottom: '2px solid #0f172a', pb: 2, mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box>
                                <Typography variant="h6" fontWeight="bold" sx={{ color: '#0f172a', letterSpacing: 0.5 }}>
                                    AFRICA FOR TOURISM
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Agence de Voyages & Tourisme — Dakar, Sénégal
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    TravelManager v2.0
                                </Typography>
                            </Box>
                            <Box textAlign="right">
                                <Typography variant="caption" display="block" color="text.secondary">
                                    Date de tirage : {dateTirage}
                                </Typography>
                                <Typography variant="caption" display="block" color="text.secondary">
                                    Page : 1 / 1
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* TITRE DU REÇU */}
                    <Box textAlign="center" sx={{ mb: 3, p: 1, bgcolor: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '4px' }}>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: '#0f172a', textTransform: 'uppercase', letterSpacing: 1 }}>
                            Reçu d'encaissement n° {numeroRecu}
                        </Typography>
                    </Box>

                    {/* INFORMATIONS PRINCIPALES */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3, fontSize: '0.9rem' }}>
                        <Box sx={{ p: 2, bgcolor: '#fafafa', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Client :</strong> {clientName}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Date d'encaissement :</strong> {formatDate(dateEncaissement)}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Enregistré par :</strong> {utilisateur}
                            </Typography>
                        </Box>

                        <Box sx={{ p: 2, bgcolor: '#fafafa', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Mode de paiement :</strong> {modePaiement}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Référence / N° pièce :</strong> {reference || '-'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 'bold' }}>
                                <strong>Montant total encaissé :</strong> {formatCurrency(montantEncaisse)} CFA
                            </Typography>
                        </Box>
                    </Box>

                    {/* TABLEAU DES FACTURES AFFECTÉES (SECTION 4.7) */}
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#0f172a' }}>
                        DÉTAIL DES FACTURES RÉGLÉES :
                    </Typography>
                    <Box sx={{ border: '1px solid #0f172a', borderRadius: '4px', overflow: 'hidden', mb: 3 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '180px 140px 1fr 160px', bgcolor: '#0f172a', color: 'white', p: 1, fontWeight: 'bold', fontSize: '0.85rem' }}>
                            <Box>N° Facture</Box>
                            <Box>Date</Box>
                            <Box>Prestation / Réf.</Box>
                            <Box sx={{ textAlign: 'right' }}>Montant affecté</Box>
                        </Box>

                        {lignes.map((l, i) => (
                            <Box
                                key={i}
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '180px 140px 1fr 160px',
                                    p: 1,
                                    borderBottom: '1px solid #e2e8f0',
                                    fontSize: '0.85rem',
                                    bgcolor: i % 2 === 0 ? 'white' : '#f8fafc'
                                }}
                            >
                                <Box sx={{ fontWeight: 600 }}>{l.numeroFacture || `F-${l.factureId}`}</Box>
                                <Box sx={{ color: 'text.secondary' }}>{formatDate(l.dateFacture)}</Box>
                                <Box sx={{ color: 'text.secondary' }}>{l.prestation || 'Règlement facture'}</Box>
                                <Box sx={{ textAlign: 'right', fontWeight: 700 }}>
                                    {formatCurrency(l.montantAffecte)} CFA
                                </Box>
                            </Box>
                        ))}

                        {/* TOTAL */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 160px', bgcolor: '#f1f5f9', p: 1.2, fontWeight: 'bold', fontSize: '0.9rem', borderTop: '2px solid #0f172a' }}>
                            <Box sx={{ textAlign: 'right', pr: 2 }}>TOTAL AFFECTÉ :</Box>
                            <Box sx={{ textAlign: 'right', color: '#0f172a' }}>{formatCurrency(montantEncaisse)} CFA</Box>
                        </Box>
                    </Box>

                    {/* SIGNATURE & CACHET */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 2, borderTop: '1px dashed #cbd5e1' }}>
                        <Box sx={{ width: '200px', textAlign: 'center' }}>
                            <Typography variant="caption" fontWeight="bold">Visa Client</Typography>
                            <Box sx={{ height: '60px' }} />
                        </Box>
                        <Box sx={{ width: '200px', textAlign: 'center' }}>
                            <Typography variant="caption" fontWeight="bold">Cachet & Signature AFT</Typography>
                            <Box sx={{ height: '60px' }} />
                        </Box>
                    </Box>
                </Paper>
            </DialogContent>

            <DialogActions sx={{ p: 2, bgcolor: '#f1f5f9' }}>
                <Button variant="outlined" onClick={onClose} sx={{ textTransform: 'none' }}>
                    Fermer
                </Button>
                <Button
                    variant="contained"
                    startIcon={<PrintIcon />}
                    onClick={handlePrint}
                    sx={{ textTransform: 'none', fontWeight: 600, bgcolor: COLORS.primary }}
                >
                    Imprimer le reçu
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RecuEncaissementModal;
